import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Package,
  Plus,
  Minus,
  Warning,
  ArrowUp,
  ArrowDown,
  ClockCounterClockwise,
  Funnel,
  Camera,
  CaretLeft,
  CaretRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import ReceiptScanner from "../components/ReceiptScanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MOV_PAGE_SIZE = 50;

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

export default function Stock() {
  const [stock, setStock] = useState([]);
  const { t, lang } = useLanguage();
  const [movements, setMovements] = useState([]);
  const [movMeta, setMovMeta] = useState({ total: 0, pages: 1, page: 1 });
  const [movPage, setMovPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const [minimumDialogOpen, setMinimumDialogOpen] = useState(false);
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const [minimumValue, setMinimumValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [receiptScannerOpen, setReceiptScannerOpen] = useState(false);

  const locale = lang === "en" ? "en-US" : lang === "es" ? "es-ES" : "pt-BR";

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchMovements(movPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movPage]);

  const fetchMovements = async (page = 1) => {
    try {
      const { data } = await axios.get(`${API}/stock/movements`, {
        params: { limit: MOV_PAGE_SIZE, skip: (page - 1) * MOV_PAGE_SIZE },
      });
      setMovements(data.items || []);
      setMovMeta({ total: data.total || 0, pages: data.pages || 1, page: data.page || 1 });
    } catch (error) {
      console.warn("Histórico de movimentos indisponível:", error?.response?.status || error?.message);
    }
  };

  const fetchData = async () => {
    try {
      const stockRes = await axios.get(`${API}/stock`);
      setStock(stockRes.data);
      await fetchMovements(movPage);
    } catch (error) {
      toast.error(t("st_load_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleEntry = async () => {
    if (!selectedIngredient || !quantity || parseFloat(quantity) <= 0) {
      toast.error(t("st_select_required"));
      return;
    }
    try {
      await axios.post(`${API}/stock/entry`, {
        ingredient_id: selectedIngredient,
        quantity: parseFloat(quantity),
        note: note || null,
      });
      toast.success(t("st_entry_success"));
      setEntryDialogOpen(false);
      setQuantity("");
      setNote("");
      setSelectedIngredient(null);
      fetchData();
    } catch (error) {
      toast.error(t("st_entry_error"));
    }
  };

  const handleExit = async () => {
    if (!selectedIngredient || !quantity || parseFloat(quantity) <= 0) {
      toast.error(t("st_select_required"));
      return;
    }
    try {
      await axios.post(`${API}/stock/exit`, {
        ingredient_id: selectedIngredient,
        quantity: parseFloat(quantity),
        note: note || null,
      });
      toast.success(t("st_exit_success"));
      setExitDialogOpen(false);
      setQuantity("");
      setNote("");
      setSelectedIngredient(null);
      fetchData();
    } catch (error) {
      toast.error(t("st_exit_error"));
    }
  };

  const handleMinimumUpdate = async () => {
    if (!selectedIngredient) return;
    try {
      await axios.put(`${API}/stock/${selectedIngredient}/minimum`, {
        minimum_stock: parseFloat(minimumValue) || 0,
      });
      toast.success(t("st_min_updated"));
      setMinimumDialogOpen(false);
      setMinimumValue("");
      setSelectedIngredient(null);
      fetchData();
    } catch (error) {
      toast.error(t("st_min_error"));
    }
  };

  const openEntryDialog = (ingredientId) => {
    setSelectedIngredient(ingredientId || null);
    setQuantity("");
    setNote("");
    setEntryDialogOpen(true);
  };

  const openExitDialog = (ingredientId) => {
    setSelectedIngredient(ingredientId || null);
    setQuantity("");
    setNote("");
    setExitDialogOpen(true);
  };

  const openMinimumDialog = (item) => {
    setSelectedIngredient(item.ingredient_id);
    setMinimumValue(String(item.minimum_stock || ""));
    setMinimumDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      ok: "bg-green-100 text-green-800 border-green-200",
      baixo: "bg-amber-100 text-amber-800 border-amber-200",
      zerado: "bg-rose-100 text-rose-800 border-rose-200",
    };
    const labels = { ok: t("st_ok"), baixo: t("st_low_badge"), zerado: t("st_zero_badge") };
    return (
      <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const filteredStock = statusFilter === "todos"
    ? stock
    : stock.filter((s) => s.status === statusFilter);

  const alerts = stock.filter((s) => s.status === "baixo" || s.status === "zerado");
  const totalStockValue = stock.reduce((sum, s) => sum + s.stock_value, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="stock-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stock-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950">{t("st_title")}</h2>
          <p className="text-xs md:text-sm text-zinc-500 mt-1">{t("st_subtitle")}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => setReceiptScannerOpen(true)}
            variant="outline"
            className="border-blue-200 text-blue-700 hover:bg-blue-50"
            data-testid="stock-scan-receipt-btn"
          >
            <Camera size={16} className="mr-2" />
            {t("st_scan_receipt")}
          </Button>
          <Button
            onClick={() => openEntryDialog(null)}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="stock-entry-btn"
          >
            <ArrowDown size={16} className="mr-2" />
            {t("st_entry")}
          </Button>
          <Button
            onClick={() => openExitDialog(null)}
            variant="outline"
            className="border-rose-200 text-rose-700 hover:bg-rose-50"
            data-testid="stock-exit-btn"
          >
            <ArrowUp size={16} className="mr-2" />
            {t("st_exit")}
          </Button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card className="border-zinc-200 shadow-sm rounded-sm">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{t("st_items")}</p>
            <p className="text-lg md:text-2xl font-mono font-bold text-zinc-900 mt-1">{stock.length}</p>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 shadow-sm rounded-sm">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{t("st_total_value")}</p>
            <p className="text-lg md:text-2xl font-mono font-bold text-zinc-900 mt-1">{formatCurrency(totalStockValue)}</p>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 shadow-sm rounded-sm">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs uppercase tracking-wider text-amber-600 font-semibold">{t("st_low")}</p>
            <p className="text-lg md:text-2xl font-mono font-bold text-amber-600 mt-1">
              {stock.filter((s) => s.status === "baixo").length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 shadow-sm rounded-sm">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs uppercase tracking-wider text-rose-600 font-semibold">{t("st_zero")}</p>
            <p className="text-lg md:text-2xl font-mono font-bold text-rose-600 mt-1">
              {stock.filter((s) => s.status === "zerado").length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 rounded-sm shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-amber-800">
              <Warning size={18} />
              {t("st_alerts_title")} ({alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {alerts.map((a) => (
                <p key={a.ingredient_id} className="text-sm text-amber-900">
                  <span className="font-semibold">{a.ingredient_name}</span>: {a.current_quantity} {a.unit}
                  {a.minimum_stock > 0 && <span className="text-amber-600"> ({t("st_min_short")}: {a.minimum_stock} {a.unit})</span>}
                </p>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="estoque" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="estoque" data-testid="tab-estoque">
            <Package size={16} className="mr-2" /> {t("st_current_tab")}
          </TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico">
            <ClockCounterClockwise size={16} className="mr-2" /> {t("st_history_tab")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="estoque" className="mt-4">
          {/* Filter */}
          <div className="flex items-center gap-2 mb-4">
            <Funnel size={16} className="text-zinc-500" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-8 text-sm" data-testid="stock-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">{t("all")}</SelectItem>
                <SelectItem value="ok">{t("st_ok")}</SelectItem>
                <SelectItem value="baixo">{t("st_low_badge")}</SelectItem>
                <SelectItem value="zerado">{t("st_zero_badge")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filteredStock.length === 0 ? (
            <div className="text-center py-12 text-zinc-500" data-testid="stock-empty">
              <Package size={48} className="mx-auto mb-3 text-zinc-300" />
              <p className="font-medium">{t("st_no_items")}</p>
              <p className="text-sm mt-1">{t("st_no_items_hint")}</p>
            </div>
          ) : (
            <div className="border border-zinc-200 rounded-sm overflow-hidden">
              <div className="overflow-x-auto"><Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="text-xs font-semibold">{t("st_ingredient")}</TableHead>
                    <TableHead className="text-xs font-semibold text-right">{t("quantity")}</TableHead>
                    <TableHead className="text-xs font-semibold text-right">{t("st_minimum")}</TableHead>
                    <TableHead className="text-xs font-semibold text-center">{t("status")}</TableHead>
                    <TableHead className="text-xs font-semibold text-right">{t("st_value")}</TableHead>
                    <TableHead className="text-xs font-semibold text-center">{t("actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStock.map((item) => (
                    <TableRow key={item.ingredient_id} data-testid={`stock-row-${item.ingredient_id}`}>
                      <TableCell>
                        <p className="text-sm font-medium text-zinc-900">{item.ingredient_name}</p>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {item.current_quantity} {item.unit}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-zinc-500">
                        {item.minimum_stock > 0 ? `${item.minimum_stock} ${item.unit}` : "—"}
                      </TableCell>
                      <TableCell className="text-center">{getStatusBadge(item.status)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(item.stock_value)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => openEntryDialog(item.ingredient_id)}
                            title={t("st_entry")}
                            data-testid={`stock-entry-${item.ingredient_id}`}
                          >
                            <Plus size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            onClick={() => openExitDialog(item.ingredient_id)}
                            title={t("st_exit")}
                            data-testid={`stock-exit-${item.ingredient_id}`}
                          >
                            <Minus size={14} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-zinc-500 hover:text-zinc-700"
                            onClick={() => openMinimumDialog(item)}
                            title={t("st_set_min_title")}
                            data-testid={`stock-minimum-${item.ingredient_id}`}
                          >
                            {t("st_min_btn")}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="historico" className="mt-4">
          {movements.length === 0 ? (
            <div className="text-center py-12 text-zinc-500" data-testid="movements-empty">
              <ClockCounterClockwise size={48} className="mx-auto mb-3 text-zinc-300" />
              <p className="font-medium">{t("st_no_movements")}</p>
              <p className="text-sm mt-1">{t("st_no_movements_hint")}</p>
            </div>
          ) : (
            <>
            <div className="border border-zinc-200 rounded-sm overflow-hidden">
              <div className="overflow-x-auto"><Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="text-xs font-semibold">{t("date")}</TableHead>
                    <TableHead className="text-xs font-semibold">{t("st_type")}</TableHead>
                    <TableHead className="text-xs font-semibold">{t("st_ingredient")}</TableHead>
                    <TableHead className="text-xs font-semibold text-right">{t("quantity")}</TableHead>
                    <TableHead className="text-xs font-semibold">{t("notes")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.map((mov) => (
                    <TableRow key={mov.id} data-testid={`movement-${mov.id}`}>
                      <TableCell className="text-xs text-zinc-500">
                        {new Date(mov.created_at).toLocaleDateString(locale)}{" "}
                        {new Date(mov.created_at).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`text-xs font-semibold px-2 py-0.5 rounded ${
                            mov.type === "entrada"
                              ? "bg-green-100 text-green-800"
                              : "bg-rose-100 text-rose-800"
                          }`}
                        >
                          {mov.type === "entrada" ? t("st_entry") : t("st_exit")}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm font-medium">{mov.ingredient_name}</TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {mov.type === "entrada" ? "+" : "-"}{mov.quantity} {mov.unit}
                      </TableCell>
                      <TableCell className="text-sm text-zinc-500">{mov.note}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></div>
            </div>
            {movMeta.pages > 1 && (
              <div className="flex items-center justify-between mt-3" data-testid="movements-pagination">
                <p className="text-xs text-zinc-500">
                  {t("pg_showing")} {movements.length} {t("of")} {movMeta.total} • {t("pg_page")} {movMeta.page}/{movMeta.pages}
                </p>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" className="h-8" disabled={movPage <= 1} onClick={() => setMovPage(movPage - 1)} data-testid="movements-prev-page">
                    <CaretLeft size={14} className="mr-1" /> {t("previous")}
                  </Button>
                  <Button variant="outline" size="sm" className="h-8" disabled={movPage >= movMeta.pages} onClick={() => setMovPage(movPage + 1)} data-testid="movements-next-page">
                    {t("next")} <CaretRight size={14} className="ml-1" />
                  </Button>
                </div>
              </div>
            )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Entry Dialog */}
      <Dialog open={entryDialogOpen} onOpenChange={setEntryDialogOpen}>
        <DialogContent data-testid="entry-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowDown size={20} className="text-green-600" />
              {t("st_register_entry")}
            </DialogTitle>
            <DialogDescription className="sr-only">{t("st_register_entry")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("st_ingredient")}</Label>
              <Select value={selectedIngredient || ""} onValueChange={setSelectedIngredient}>
                <SelectTrigger className="mt-1.5" data-testid="entry-ingredient-select">
                  <SelectValue placeholder={t("st_select_placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  {stock.map((item) => (
                    <SelectItem key={item.ingredient_id} value={item.ingredient_id}>
                      {item.ingredient_name} ({item.current_quantity} {item.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("quantity")}</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="Ex: 1000"
                className="mt-1.5"
                data-testid="entry-quantity-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("st_note_opt")}</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="mt-1.5"
                data-testid="entry-note-input"
              />
            </div>
            <Button
              onClick={handleEntry}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              data-testid="confirm-entry-btn"
            >
              {t("st_confirm_entry")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exit Dialog */}
      <Dialog open={exitDialogOpen} onOpenChange={setExitDialogOpen}>
        <DialogContent data-testid="exit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUp size={20} className="text-rose-600" />
              {t("st_register_exit")}
            </DialogTitle>
            <DialogDescription className="sr-only">{t("st_register_exit")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("st_ingredient")}</Label>
              <Select value={selectedIngredient || ""} onValueChange={setSelectedIngredient}>
                <SelectTrigger className="mt-1.5" data-testid="exit-ingredient-select">
                  <SelectValue placeholder={t("st_select_placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  {stock.map((item) => (
                    <SelectItem key={item.ingredient_id} value={item.ingredient_id}>
                      {item.ingredient_name} ({item.current_quantity} {item.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("quantity")}</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="Ex: 500"
                className="mt-1.5"
                data-testid="exit-quantity-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("st_note_opt")}</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="mt-1.5"
                data-testid="exit-note-input"
              />
            </div>
            <Button
              onClick={handleExit}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="confirm-exit-btn"
            >
              {t("st_confirm_exit")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Minimum Dialog */}
      <Dialog open={minimumDialogOpen} onOpenChange={setMinimumDialogOpen}>
        <DialogContent data-testid="minimum-dialog">
          <DialogHeader>
            <DialogTitle>{t("st_define_min")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t("st_min_qty")}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={minimumValue}
                onChange={(e) => setMinimumValue(e.target.value)}
                placeholder="Ex: 500"
                className="mt-1.5"
                data-testid="minimum-value-input"
              />
              <p className="text-xs text-zinc-500 mt-1">{t("st_min_hint")}</p>
            </div>
            <Button onClick={handleMinimumUpdate} className="w-full" data-testid="confirm-minimum-btn">
              {t("save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Scanner Modal */}
      <ReceiptScanner
        open={receiptScannerOpen}
        onOpenChange={setReceiptScannerOpen}
        onComplete={fetchData}
      />
    </div>
  );
}
